import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, RotateCcw } from 'lucide-react';

const VideoPlayer = ({ src, title, onClose, isFullPage = false }) => {
	const videoRef = useRef(null);
	const containerRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [currentTime, setCurrentTime] = useState(0);
	const [duration, setDuration] = useState(0);
	const [volume, setVolume] = useState(1);
	const [isMuted, setIsMuted] = useState(false);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const [showControls, setShowControls] = useState(true);
	const [isLoading, setIsLoading] = useState(true);
	const controlsTimeoutRef = useRef(null);

	// Prevent right-click context menu
	const handleContextMenu = e => {
		e.preventDefault();
		return false;
	};

	// Play/Pause toggle
	const togglePlay = useCallback(() => {
		if (videoRef.current) {
			if (isPlaying) {
				videoRef.current.pause();
			} else {
				videoRef.current.play();
			}
			setIsPlaying(!isPlaying);
		}
	}, [isPlaying]);

	// Handle time update
	const handleTimeUpdate = () => {
		if (videoRef.current) {
			setCurrentTime(videoRef.current.currentTime);
		}
	};

	// Handle loaded metadata
	const handleLoadedMetadata = () => {
		if (videoRef.current) {
			setDuration(videoRef.current.duration);
			setIsLoading(false);
		}
	};

	// Handle seek
	const handleSeek = e => {
		const rect = e.currentTarget.getBoundingClientRect();
		const percent = (e.clientX - rect.left) / rect.width;
		const newTime = percent * duration;
		if (videoRef.current) {
			videoRef.current.currentTime = newTime;
			setCurrentTime(newTime);
		}
	};

	// Handle volume change
	const handleVolumeChange = e => {
		const newVolume = parseFloat(e.target.value);
		setVolume(newVolume);
		if (videoRef.current) {
			videoRef.current.volume = newVolume;
		}
		setIsMuted(newVolume === 0);
	};

	// Toggle mute
	const toggleMute = () => {
		if (videoRef.current) {
			if (isMuted) {
				videoRef.current.volume = volume || 1;
				setIsMuted(false);
			} else {
				videoRef.current.volume = 0;
				setIsMuted(true);
			}
		}
	};

	// Toggle fullscreen
	const toggleFullscreen = useCallback(() => {
		if (!document.fullscreenElement) {
			if (containerRef.current?.requestFullscreen) {
				containerRef.current.requestFullscreen();
			}
		} else {
			if (document.exitFullscreen) {
				document.exitFullscreen();
			}
		}
	}, []);

	// Handle fullscreen change
	useEffect(() => {
		const handleFullscreenChange = () => {
			setIsFullscreen(!!document.fullscreenElement);
		};

		document.addEventListener('fullscreenchange', handleFullscreenChange);
		return () => {
			document.removeEventListener('fullscreenchange', handleFullscreenChange);
		};
	}, []);

	// Format time
	const formatTime = time => {
		const minutes = Math.floor(time / 60);
		const seconds = Math.floor(time % 60);
		return `${minutes}:${seconds.toString().padStart(2, '0')}`;
	};

	// Auto-hide controls
	const handleMouseMove = () => {
		setShowControls(true);
		if (controlsTimeoutRef.current) {
			clearTimeout(controlsTimeoutRef.current);
		}
		if (isPlaying) {
			controlsTimeoutRef.current = setTimeout(() => {
				setShowControls(false);
			}, 3000);
		}
	};

	// Restart video
	const handleRestart = () => {
		if (videoRef.current) {
			videoRef.current.currentTime = 0;
			videoRef.current.play();
			setIsPlaying(true);
		}
	};

	// Handle video end
	const handleEnded = () => {
		setIsPlaying(false);
		setShowControls(true);
	};

	// Keyboard controls
	useEffect(() => {
		const handleKeyDown = e => {
			if (e.key === ' ' || e.key === 'k') {
				e.preventDefault();
				togglePlay();
			} else if (e.key === 'f') {
				toggleFullscreen();
			} else if (e.key === 'm') {
				toggleMute();
			} else if (e.key === 'ArrowLeft') {
				if (videoRef.current) {
					videoRef.current.currentTime -= 10;
				}
			} else if (e.key === 'ArrowRight') {
				if (videoRef.current) {
					videoRef.current.currentTime += 10;
				}
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [togglePlay, toggleFullscreen]);

	const progressPercent = duration ? (currentTime / duration) * 100 : 0;

	return (
		<div
			ref={containerRef}
			className={`video-player-container ${isFullscreen ? 'fullscreen' : ''} ${isFullPage ? 'full-page' : ''}`}
			onMouseMove={handleMouseMove}
			onMouseLeave={() => isPlaying && setShowControls(false)}
		>
			{title && <div className="video-player-title">{title}</div>}

			<div className="video-wrapper" onClick={togglePlay}>
				{isLoading && (
					<div className="video-loading">
						<div className="loading-spinner"></div>
					</div>
				)}

				<video
					ref={videoRef}
					src={src}
					onTimeUpdate={handleTimeUpdate}
					onLoadedMetadata={handleLoadedMetadata}
					onEnded={handleEnded}
					onContextMenu={handleContextMenu}
					controlsList="nodownload nofullscreen noremoteplayback"
					disablePictureInPicture
					playsInline
				/>

				{!isPlaying && !isLoading && (
					<div className="play-overlay">
						<button className="play-overlay-btn" onClick={togglePlay}>
							<Play size={48} fill="#fff" />
						</button>
					</div>
				)}
			</div>

			<div className={`video-controls ${showControls ? 'visible' : ''}`}>
				<div className="progress-bar" onClick={handleSeek}>
					<div className="progress-track">
						<div className="progress-filled" style={{ width: `${progressPercent}%` }} />
					</div>
				</div>

				<div className="controls-row">
					<div className="controls-left">
						<button className="control-btn" onClick={togglePlay}>
							{isPlaying ? <Pause size={20} /> : <Play size={20} />}
						</button>
						<button className="control-btn" onClick={handleRestart}>
							<RotateCcw size={18} />
						</button>
						<div className="volume-control">
							<button className="control-btn" onClick={toggleMute}>
								{isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
							</button>
							<input
								type="range"
								min="0"
								max="1"
								step="0.1"
								value={isMuted ? 0 : volume}
								onChange={handleVolumeChange}
								className="volume-slider"
							/>
						</div>
						<span className="time-display">
							{formatTime(currentTime)} / {formatTime(duration)}
						</span>
					</div>

					<div className="controls-right">
						<button className="control-btn" onClick={toggleFullscreen}>
							{isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default VideoPlayer;
